import type {
  Audience,
  IExperienceTemplate,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import type { Rule } from '@dadohr/shared/domainTypes/rules';
import type { ITargetAudience } from '@dadohr/shared/domainTypes/targetAudience';
import { compact, isEqual, sortBy } from 'lodash';

/**
 * Pure logic behind the timeline's "Filter by Segment" row.
 *
 * A saved segment (targetAudiencesV1) is copied BY VALUE onto a task /
 * message / automation as an `Audience` (fresh id, "<name> (copy)", cloned
 * rule) — see Builders/TaskBuilder/components/createAudienceFromSegment.ts.
 * There is deliberately no back-reference id, so the only read-only way to
 * recognise "this item audience is segment S" is structural equality of the
 * rule trees. An edited copy therefore stops matching, which is correct: it
 * is no longer that segment.
 *
 * Kept free of UI / service imports so it is unit-testable under Jest
 * (importing e.g. Audiences.tsx drags in src/sentry.ts and breaks the
 * transform — same constraint as createAudienceFromSegment.ts).
 */

export type SegmentRule = Rule<UserField>;

export type SegmentDescriptor = {
  /** Saved segment (targetAudiencesV1) id. */
  id: string;
  /** Saved segment name — NOT the "(copy)" name of the item audience. */
  label: string;
  /** Color of the first matching item audience, else DEFAULT_SEGMENT_PILL_COLOR. */
  color: string;
  rule: SegmentRule;
};

/** Minimal shape shared by ITaskTemplate, ExperienceTemplateMessage and IAutomationTemplate. */
export type AudienceCarrier = { id: string; audiences?: Audience[] };

// Mirrors DEFAULT_AUDIENCE_COLOR in Builders/TaskBuilder/components/Audiences.tsx.
// Duplicated on purpose: importing that file breaks Jest (see header comment).
export const DEFAULT_SEGMENT_PILL_COLOR = '#BBC1C7';

const isSegmentAudience = (a: Audience) => a.rule !== 'EVERYONE';

// An audience with zero blocks delivers nothing — it doesn't matter whether
// its rule matches a segment if there's no content behind it.
const hasContent = (a: Audience) => a.blocks.length > 0;

const activeAudiences = (audiences: Audience[] | undefined) =>
  (audiences ?? []).filter((a) => !a.inactive);

/** True when an item audience's rule is structurally identical to a saved segment's rule. */
export const rulesEqual = (
  audienceRule: Audience['rule'],
  segmentRule: SegmentRule
): boolean => audienceRule !== 'EVERYONE' && isEqual(audienceRule, segmentRule);

/** tasks + messages (other, outro) + automations — every template item that carries audiences. */
export const getTemplateAudienceCarriers = (
  tpl: Pick<IExperienceTemplate, 'tasks' | 'messages' | 'automations'>
): AudienceCarrier[] => [
  ...tpl.tasks,
  ...compact([...tpl.messages.other, tpl.messages.outro]),
  ...tpl.automations
];

/**
 * Pills for the row: one per saved segment that at least one (non-inactive)
 * item audience structurally matches — regardless of how much content is
 * currently assigned to that audience, including none. A pill has to exist
 * even for an empty match: that's what lets someone turn it on and see that
 * this template currently sends NOTHING to that segment, which is as
 * important a thing to reveal as what it does send. (Whether the item
 * itself then shows or hides is `itemVisibleForSegment`'s job, below — an
 * empty matching audience correctly hides its item once the pill is
 * active.) Saved segments nobody copied at all still produce no pill, so a
 * pill can never filter to a timeline that was never going to show anything
 * different in the first place. Sorted by label — Firestore collection
 * order is unspecified.
 */
export const getUsedSegments = (
  items: AudienceCarrier[],
  segments: Pick<ITargetAudience, 'id' | 'name' | 'rule'>[]
): SegmentDescriptor[] => {
  const allAudiences = items.flatMap((it) =>
    activeAudiences(it.audiences).filter(isSegmentAudience)
  );
  const used = compact(
    segments.map((seg) => {
      const match = allAudiences.find((a) => rulesEqual(a.rule, seg.rule));
      if (!match) return null;
      return {
        id: seg.id,
        label: seg.name,
        color: match.color || DEFAULT_SEGMENT_PILL_COLOR,
        rule: seg.rule
      };
    })
  );
  return sortBy(used, (s) => s.label.toLowerCase());
};

/**
 * Reach-based visibility: would a member of the segment receive this item?
 *
 * Mirrors the real per-user resolution (`getAudienceForUser` in
 * services/experiences/audience.ts): a person is resolved to whichever
 * defined audience's RULE matches them, full stop — content never enters
 * into which audience they land on, and once a rule matches them they never
 * fall through to Everyone / "Everyone else". Only someone matched by NO
 * defined rule falls through to Everyone.
 *
 * So, for a hypothetical member of the segment:
 *  - if some (non-inactive) audience's rule is exactly this segment, THAT
 *    audience alone decides visibility — shown iff it has ≥1 block. An
 *    empty matching audience means members of the segment see nothing,
 *    and they do NOT fall back to Everyone / "Everyone else" just because
 *    it happens to have content — that content is for people NOT captured
 *    by any specific rule, which a segment member already was.
 *  - otherwise (no audience targets this segment at all — including the
 *    "no segment audiences on this item" case), they fall through to
 *    Everyone / "Everyone else" — shown iff it has ≥1 block.
 *
 * Never throws (unlike getEveryoneAudience): an item with targeted audiences
 * but no Everyone audience simply has nothing for anyone not specifically
 * targeted, and is hidden for them.
 *
 * Deactivated items are NOT special-cased here — they stay visible (greyed)
 * on the timeline today, and this filter answers "is this content aimed at
 * the segment", not "will it materialise right now".
 */
export const itemVisibleForSegment = (
  audiences: Audience[] | undefined,
  segmentRule: SegmentRule
): boolean => {
  const active = activeAudiences(audiences);
  const targeted = active.filter(isSegmentAudience);
  if (targeted.length === 0) return true;
  const matched = targeted.find((a) => rulesEqual(a.rule, segmentRule));
  if (matched) return hasContent(matched);
  const everyone = active.find((a) => a.rule === 'EVERYONE');
  return !!everyone && hasContent(everyone);
};

/** Ids of every item a member of the segment would receive. Item ids double as timeline item keys. */
export const getVisibleItemIdsForSegment = (
  items: AudienceCarrier[],
  segmentRule: SegmentRule
): Set<string> =>
  new Set(
    items
      .filter((it) => itemVisibleForSegment(it.audiences, segmentRule))
      .map((it) => it.id)
  );
